import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, LogOut, User, LayoutDashboard, List, Crown, Search, BookOpen, Building2, Users, ChevronDown, PawPrint } from "lucide-react";
import NotificationCenter from "./NotificationCenter";

const C = {
  navy: "#2D3561", white: "#FAFAF8", orange: "#F5A623",
  blue: "#4A90D9", red: "#E74C3C", pink: "#F78DA7",
  lilac: "#B39DDB", gray: "#6B7280",
};
const DISPLAY = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";

export default function Navbar() {
  const { user, logout, activeRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    const token = localStorage.getItem("ea_token");
    if (token) {
      axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setIsAdmin(res.data.is_admin))
        .catch(() => setIsAdmin(false));
    }
  }, [user]);

  const handleLogout = () => { logout(); navigate("/"); };

  const path = location.pathname;
  const appLabel = path.startsWith("/prepara") ? "PREPARA" : path.startsWith("/aprende") ? "APRENDE" : "CONECTA";
  const appColor = path.startsWith("/prepara") ? "#81801a" : path.startsWith("/aprende") ? C.lilac : C.blue;
  const dashPath = activeRole === "protectora" ? "/shelter" : activeRole === "admin" ? "/admin" : "/dashboard";

  const ROLE_OPTIONS = [
    { value: "particular", label: "Voluntaria", icon: Users, color: C.blue },
    { value: "protectora", label: "Protectora", icon: Building2, color: "#81801a" },
    { value: "admin", label: "Admin", icon: Crown, color: C.orange },
  ];
  const currentRoleOption = ROLE_OPTIONS.find(r => r.value === activeRole) || ROLE_OPTIONS[0];

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
      style={{ background: C.white, borderBottom: `1px solid ${C.navy}12`, fontFamily: DISPLAY }}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-11 h-11 flex items-center justify-center overflow-hidden" style={{ borderRadius: 4 }}>
            <img src="/logos/logo-conecta.png" alt="EA" className="w-full h-full object-contain" />
          </div>
          <div className="hidden sm:block whitespace-nowrap">
            <span className="text-lg font-bold tracking-tight" style={{ color: C.navy }}>EMERGENCIA ANIMAL</span>
            <span className="text-lg font-bold tracking-tight ml-1.5" style={{ color: appColor }}>{appLabel}</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {[
            { to: "/needs", label: "Necesidades", testId: "nav-needs-link" },
            { to: "/lost-pets", label: "Se busca", icon: Search, testId: "nav-lost-pets-link" },
            { to: "/fauna", label: "Fauna", testId: "nav-fauna-link", accent: true },
            { to: "/tiempo", label: "Tiempo", testId: "nav-tiempo-link" },
            { to: "/aprende/kids", label: "Kids", testId: "nav-kids-link", accent: true },
            { to: "/recursos/ciudadania/guias", label: "Guías", icon: BookOpen, testId: "nav-guides-link" },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={link.testId}
              className="px-4 py-2 text-[13px] font-semibold uppercase tracking-[0.1em] flex items-center gap-1.5 transition-colors duration-200 whitespace-nowrap"
              style={{
                fontFamily: MONO,
                color: link.accent ? C.pink : C.gray,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = C.navy; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = link.accent ? C.pink : C.gray; }}
            >
              {link.accent && <PawPrint className="w-4 h-4" />}
              {link.icon && !link.accent && <link.icon className="w-4 h-4" />}
              {link.label}
            </Link>
          ))}

          {user && <NotificationCenter />}

          {/* Role Switcher */}
          {user && isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 px-3 py-1 ml-1 transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: currentRoleOption.color, color: "#fff", borderRadius: 4, fontFamily: MONO }}
                  data-testid="nav-role-switcher"
                >
                  <currentRoleOption.icon className="w-3 h-3" />
                  <span className="text-[11px] font-bold tracking-wider">{currentRoleOption.label}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" style={{ borderRadius: 4, border: `1px solid ${C.navy}12` }}>
                {ROLE_OPTIONS.map((role) => (
                  <DropdownMenuItem
                    key={role.value}
                    onClick={() => {
                      setActiveRole(role.value);
                      if (role.value === "admin") navigate("/admin");
                      else if (role.value === "protectora") navigate("/shelter");
                      else navigate("/dashboard");
                    }}
                    className={`font-medium ${activeRole === role.value ? "bg-gray-50" : ""}`}
                    style={{ fontFamily: MONO, fontSize: 11 }}
                    data-testid={`role-switch-${role.value}`}
                  >
                    <role.icon className="w-3.5 h-3.5 mr-2" style={{ color: role.color }} />
                    <span style={{ color: role.color }}>{role.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-1.5 ml-1 transition-all duration-200 hover:-translate-y-0.5"
                  style={{ background: C.navy, color: "#fff", borderRadius: 4 }}
                  data-testid="nav-user-menu"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-5 h-5 object-cover" style={{ borderRadius: 3 }} />
                  ) : (
                    <div className="w-5 h-5 flex items-center justify-center" style={{ background: C.blue, borderRadius: 3 }}>
                      <span className="text-white text-[9px] font-bold">{user.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-[10px] font-bold tracking-wider" style={{ fontFamily: MONO }}>{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" style={{ borderRadius: 4, border: `1px solid ${C.navy}12` }}>
                <DropdownMenuItem onClick={() => navigate(dashPath)} data-testid="nav-dashboard-link" style={{ fontFamily: MONO, fontSize: 11 }}>
                  <LayoutDashboard className="w-3.5 h-3.5 mr-2" /> Mi panel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/needs")} data-testid="nav-dropdown-needs" style={{ fontFamily: MONO, fontSize: 11 }}>
                  <List className="w-3.5 h-3.5 mr-2" /> Necesidades
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-btn" style={{ fontFamily: MONO, fontSize: 11, color: C.red }}>
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => navigate("/auth")}
              className="ml-1 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
              style={{ fontFamily: MONO, background: C.navy, color: "#fff", borderRadius: 4 }}
              data-testid="nav-login-btn"
            >
              Entrar
            </button>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationCenter />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 flex items-center justify-center" style={{ border: `1px solid ${C.navy}15`, borderRadius: 4 }} data-testid="nav-mobile-menu">
                <Menu className="w-5 h-5" style={{ color: C.navy }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" style={{ borderRadius: 4, border: `1px solid ${C.navy}12` }}>
              <DropdownMenuItem onClick={() => navigate("/needs")} style={{ fontFamily: MONO, fontSize: 11 }}>
                <List className="w-3.5 h-3.5 mr-2" /> Necesidades
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/lost-pets")} style={{ fontFamily: MONO, fontSize: 11, color: C.pink }}>
                <Search className="w-3.5 h-3.5 mr-2" /> Se busca
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/recursos/ciudadania/guias")} style={{ fontFamily: MONO, fontSize: 11 }}>
                <BookOpen className="w-3.5 h-3.5 mr-2" /> Guías
              </DropdownMenuItem>
              {user ? (
                <>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      {ROLE_OPTIONS.map((role) => (
                        <DropdownMenuItem
                          key={role.value}
                          onClick={() => {
                            setActiveRole(role.value);
                            if (role.value === "admin") navigate("/admin");
                            else if (role.value === "protectora") navigate("/shelter");
                            else navigate("/dashboard");
                          }}
                          style={{ fontFamily: MONO, fontSize: 11 }}
                        >
                          <role.icon className="w-3.5 h-3.5 mr-2" style={{ color: role.color }} />
                          <span style={{ color: role.color }}>{role.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(dashPath)} style={{ fontFamily: MONO, fontSize: 11 }}>
                    <LayoutDashboard className="w-3.5 h-3.5 mr-2" /> Mi panel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} style={{ fontFamily: MONO, fontSize: 11, color: C.red }}>
                    <LogOut className="w-3.5 h-3.5 mr-2" /> Salir
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => navigate("/auth")} style={{ fontFamily: MONO, fontSize: 11 }}>
                  <User className="w-3.5 h-3.5 mr-2" /> Entrar
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
